//  Generic imports
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";

//  Motif imports
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifTooltip,
  MotifButton,
} from "@ey-xd/motif-react";
import { MotifIconButton } from "@ey-xd/motif-react/Button";
import MotifToast from "@ey-xd/motif-react/Toast";
import MotifInlineMessage from "@ey-xd/motif-react/InlineMessage";

//  Internal imports
import "./Commentry.scss";
import Utils from "../../../utils/Utils";
import TableService from "../../../services/tableService";
import AnalysisService from "../../../services/analysisService";
import {tableTypePublishCopy,tableTypePublishLike,tableTypePublishDisLike,tableTypePublishTPPT,tableTypePublishSourceLines } from "../../../../src/config/constants";

//  Icons imports
import iconSparks from "../../../assets/images/sparks.svg";
import iconThumbsUp from "../../../assets/images/thumbs-up.svg";
import iconThumbsDown from "../../../assets/images/thumbs-down.svg";
import iconThumbsUpFilled from "../../../assets/images/thumbs-up-filled.svg";
import iconThumbsDownFilled from "../../../assets/images/thumbs-down-filled.svg";
import iconCopy from "../../../assets/images/copy-icon-commentary.svg";
import iconTransfer from "../../../assets/images/transfer-icon.svg";
import iconWarningInfoCircle from "../../../assets/images/warning_info_circle.svg";
import iconCollaps from "../../../assets/images/collaps.svg";
import iconCheckCircle from "../../../assets/images/check-circle.svg";
import iconExpand from "../../../assets/images/expand-icon.svg";
import iconSourceLines from "../../../assets/images/source_lines.svg";
import iconWarningError from "../../../assets/images/warning_error.svg";
import iconLineCircleRed from "../../../assets/images/line_circle_red.svg";
import iconInfo from "../../../assets/images/info-empty.svg";

export default function Commentary(props) {
  const {
    isTableCommentaryInitiated,
    isTableCommentaryGenerated,
    isTableAnalysisGenerated,
    isAllLineAnalysisNotGenerated,
    lineAnalysisStatus,
    showTransferToastTableAnalysis,
    isCommentaryAvailable,
    handleTransferToPPTClick,
    showToastTableAnalysis,
    setShowToastTableAnalysis,
    setShowTableAnalysisModal,
    showTableAnalysisModal,
    showToastCopy,
    setShowToastCopy,
    isTableUnpublished
  } = props;
  const [showToastMessage, setShowToastMessage] = useState(false);
  const [showSourceLines, setShowSourceLines] = useState(false);
  const [commentaryLiked, setCommentaryLiked] = useState(0);
  const [rowStatusData, setRowStatusData] = useState([]);
  const { tableAnalysis } = useSelector((state) => state.analysis);
  const tableService = new TableService();
  const analysisService = new AnalysisService();
  const { user, activeProject } = useSelector((state) => state.user);
  const { selectedTable } = useSelector((state) => state.table);
  const utils = new Utils();
  useEffect(() => {
    setCommentaryLiked(tableAnalysis?.feedback);
  }, [tableAnalysis]);

  const copyText = () => {
    const text = document.getElementById("commentary-para").innerText;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        if (showTableAnalysisModal) {
          setShowToastCopy(true);
          setShowToastTableAnalysis(false);
        } else {
          setShowToastMessage(true);
        }
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);
      });
  };

  const handleLikeClick = async () => {
    const data = {
      projectId: activeProject.project_UUID,
      tableId: props.selectedTableData.id,
      rowId: "NA",
      queueMethod: "tableAnalysis",
      status: "finished",
      feedback: commentaryLiked === 1 ? 0 : 1,
    };
    const feedback = await tableService.submitFeedback(data);
    if (feedback) {
      if (commentaryLiked === 1) {
        setCommentaryLiked(0);
      } else {
        setCommentaryLiked(1);
      }
    }
  };

  const handleDisLikeClick = async () => {
    const data = {
      projectId: activeProject.project_UUID,
      tableId: props.selectedTableData.id,
      rowId: "NA",
      queueMethod: "tableAnalysis",
      status: "finished",
      feedback: commentaryLiked === -1 ? 0 : -1,
    };
    const feedback = await tableService.submitFeedback(data);
    if (feedback) {
      if (commentaryLiked === -1) {
        setCommentaryLiked(0);
      } else {
        setCommentaryLiked(-1);
      }
    }
  };
  const setTableAnalysisModal = () => {
    setShowTableAnalysisModal(true);
    setShowToastTableAnalysis(false);
    setShowToastCopy(false);
  };
  const setSourceLinesModal = () => {
    setShowSourceLines(true);
    getRowStatus();
  };

  const projectId = activeProject.project_UUID;
  const userId = user.username;
  const tableId = selectedTable.id;

  const getRowStatus = async () => {
    const data = await analysisService.getRowStatus(tableId, projectId, userId);
    if (data && Object.keys(data).length > 0) {
      setRowStatusData(data.rowIds);
    }
  };

  const filteredRows = props.selectedTableData?.tableContent
    ?.filter((row) =>
      rowStatusData.some(
        (status) =>
          status.rowId === row.rowId &&
          status.usedForAnalysis
      )
    )
    .map((row) => ({
      RowHeader: row.RowHeader,
      status:
        rowStatusData.find((status) => status.rowId === row.rowId)?.status ||
        "Unknown",
      rowId: row.rowId,
    }));

  return (
    <>
      {showTableAnalysisModal && (
        <MotifModal
          show={setShowTableAnalysisModal}
          onClose={() => setShowTableAnalysisModal(!showTableAnalysisModal)}
          focusTrapOptions={{
            tabbableOptions: {
              displayCheck: "none",
            },
          }}
          className=" genericModal tableanalysispopover"
          size="xl"
        >
          <MotifModalHeader
            icons={{
              close: (
                <MotifTooltip
                  placement="left"
                  contentClassName="tooltipWithoutClose tooltipLM075"
                  hideCloseButton={true}
                  allowClick={false}
                  trigger={<img src={iconCollaps} alt="Collapse" />}
                >
                  Collapse
                </MotifTooltip>
              ),
            }}
            className="lineAnalysisPopoverHeader"
          >
            <div>Table Analysis</div>
          </MotifModalHeader>

          <MotifModalBody className="tableanalysispopoverbody">
            <div className="top-bar-commentry-icons">
              <div>
                <MotifIconButton className="top-icons btnGenAI">
                  <MotifTooltip
                    placement="bottom"
                    contentClassName="tooltipWithoutClose"
                    hideCloseButton={true}
                    allowClick={false}
                    trigger={<img src={iconSparks} alt="AI" />}
                  >
                    AI Generated
                  </MotifTooltip>
                </MotifIconButton>
                <MotifIconButton
                  className="top-icons btnLike"
                  onClick={handleLikeClick}
                  disabled={((activeProject.category !== "3") && isTableUnpublished)}
                >
                  <MotifTooltip
                    placement="bottom"
                    contentClassName="tooltipWithoutClose"
                    hideCloseButton={true}
                    allowClick={false}
                    trigger={
                      commentaryLiked === 1 ? (
                        <img src={iconThumbsUpFilled} alt="Liked" />
                      ) : (
                        <img src={iconThumbsUp} alt="Like" />
                      )
                    }
                  >
                    {((activeProject.category !== "3") && isTableUnpublished)? tableTypePublishLike: "Like"}
                  </MotifTooltip>
                </MotifIconButton>
                <MotifIconButton
                  className="top-icons btnDislike"
                  disabled={((activeProject.category !== "3") && isTableUnpublished)}
                  onClick={handleDisLikeClick}
                >
                  <MotifTooltip
                    placement="bottom"
                    contentClassName="tooltipWithoutClose"
                    hideCloseButton={true}
                    allowClick={false}
                    trigger={
                      commentaryLiked === -1 ? (
                        <img src={iconThumbsDownFilled} alt="Disliked" />
                      ) : (
                        <img src={iconThumbsDown} alt="Dislike" />
                      )
                    }
                  >
                {((activeProject.category !== "3") && isTableUnpublished) ? tableTypePublishDisLike : "Dislike"}
                  </MotifTooltip>
                </MotifIconButton>
                <MotifIconButton
                  className="top-icons btnCopy"
                  onClick={copyText}
                  disabled={((activeProject.category !== "3") && isTableUnpublished)}
               >
                  <MotifTooltip
                    placement="bottom"
                    contentClassName="tooltipWithoutClose"
                    hideCloseButton={true}
                    allowClick={false}
                    trigger={<img src={iconCopy} alt="Copy" className="lpv" />}
                    allowHover={true}
                  >
                    {((activeProject.category !== "3") && isTableUnpublished) ? tableTypePublishCopy : "Copy"}
                  </MotifTooltip>
                </MotifIconButton>
                <MotifIconButton
                  className="top-icons btnTransfer"
                  onClick={() => {
                    handleTransferToPPTClick(false);
                  }}
                  disabled={
                    ((activeProject.category !== "3") && isTableUnpublished) ||
                    (isTableCommentaryInitiated && !isTableCommentaryGenerated) ||
                    !isTableAnalysisGenerated ||
                    showToastTableAnalysis ||
                    (isTableAnalysisGenerated === "In-Progress") ||
                    (isAllLineAnalysisNotGenerated && !isTableCommentaryGenerated) ||
                    ((lineAnalysisStatus === "In-Progress") && !isTableCommentaryGenerated) ||
                    showTransferToastTableAnalysis ||
                    !isCommentaryAvailable("Table")
                  }
                >
                  <MotifTooltip
                    placement="bottom"
                    contentClassName="tooltipWithoutClose"
                    hideCloseButton={true}
                    allowClick={false}
                    trigger={<img src={iconTransfer} alt="Transfer" />}
                  >
                    {((activeProject.category !== "3") && isTableUnpublished) ? tableTypePublishTPPT : "Transfer to PPT"}
                  </MotifTooltip>
                </MotifIconButton>
              </div>
            </div>
            {tableAnalysis?.answer && (
              <p
                className="commentry-paragraph"
                id="commentary-para"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    utils.markdownToHtml(tableAnalysis.answer)
                  ),
                }}
              ></p>
            )}
            <MotifInlineMessage
              variant="warning"
              icons={{
                message: (
                  <img
                    src={iconWarningInfoCircle}
                    className="iconWarningInfoCircle"
                    alt="Warning"
                  />
                ),
              }}
              className="msgAIContainer"
            >
              <span className="msgAI">
                AI generated output may contain inaccuracies or errors. Verify
                output before use.
              </span>
            </MotifInlineMessage>
          </MotifModalBody>
          {showToastCopy && (
            <MotifToast
              onClose={() => {
                setShowToastCopy(false);
              }}
              variant="success"
              icons={{ toast: <img src={iconCheckCircle} alt="Success" /> }}
            >
              Copied
            </MotifToast>
          )}
          {showToastTableAnalysis && (
            <MotifToast
              onClose={() => {
                setShowToastTableAnalysis(false);
              }}
              variant="success"
              icons={{ toast: <img src={iconCheckCircle} alt="Success" /> }}
            >
              Transfer successful
            </MotifToast>
          )}
        </MotifModal>
      )}
      {showSourceLines && (
        <MotifModal
          show={setShowSourceLines}
          onClose={() => setShowSourceLines(!showSourceLines)}
          focusTrapOptions={{
            tabbableOptions: {
              displayCheck: "none",
            },
          }}
          className="genericModal tableanalysispopover modalMotifFooterModal"
          size="xl"
        >
          <MotifModalHeader
            closeButtonProps={{
              "aria-label": "Close",
              title: "Close",
            }}
            className="lineAnalysisPopoverHeader"
          >
            <div>Table Analysis Source Lines</div>
          </MotifModalHeader>
          <MotifModalBody className="tableanalysispopoverbody">
            <div className="tabContentTables" id="idTabContentTables">
              {filteredRows.map((vLineItem) => (
                <div key={vLineItem.rowId} className="tableRow">
                  <div className= {vLineItem.status === "Disabled"? "accordionTitle acc-disable":"accordionTitle"}>{vLineItem.RowHeader}</div>
                  <div className="status-div">
                    {(() => {
                      const rowData =
                        rowStatusData.find(
                          (row) => row.rowId === vLineItem.rowId
                        ) || {};
                      const status = rowData.status;

                      const statusIcons = {
                        Completed:
                          rowData.rowdocumentInd === 0 ||
                          rowData.rowrelationshipInd === 0 ||
                          rowData.rowkpiInd === 0 ||
                          rowData.rownotesInd === 0 ? (
                            <MotifTooltip
                              hideCloseButton="true"
                              placement="left"
                              className="icons-status"
                              trigger={
                                <span className="icons">
                                <img
                                  className="icon-check-red"
                                  src={iconLineCircleRed}
                                  alt="Completed"
                                />
                                </span>
                              }
                            >
                              Change in analysis settings
                            </MotifTooltip>
                          ) : (
                            <span className="icons">
                            <img
                              className="icon-check-green"
                              src={iconCheckCircle}
                              alt="Completed"
                            />
                            </span>
                          ),
                        Failure: (
                          <MotifTooltip
                            hideCloseButton="true"
                            placement="left"
                            trigger={
                              <span className="icons">
                              <img
                                src={iconWarningError}
                                alt="Error"
                                className="errorMsg"
                              />
                              </span>
                            }
                          >
                            Error in Line Analysis
                          </MotifTooltip>
                        ),
                        Disabled: (
                          <MotifTooltip
                            trigger={
                              <span className="icons">
                              <img
                                src={iconInfo}
                                alt="Help"
                                className="iconInfo"
                              />
                              </span>
                            }
                            allowClick={false}
                            hideCloseButton={true}
                            placement="left"
                          >
                            Row excluded due <br />
                            to applied thresholds
                          </MotifTooltip>
                        ),
                      };

                      return statusIcons[status] || null;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </MotifModalBody>
          <MotifModalFooter className="btn-Cls">
            <MotifButton
              onClick={() => {
                setShowSourceLines(!showSourceLines);
              }}
              type="button"
              variant="secondary"
              className="btn-close"
            >
              Close
            </MotifButton>
          </MotifModalFooter>
        </MotifModal>
      )}
      <div className="container">
        <div className="commentry-content">
          <div className="top-bar-commentry">
            <div>
              <MotifIconButton className="top-icons btnGenAI">
                <MotifTooltip
                  placement="bottom"
                  contentClassName="tooltipWithoutClose"
                  hideCloseButton={true}
                  allowClick={false}
                  trigger={<img src={iconSparks} alt="AI" />}
                >
                  AI Generated
                </MotifTooltip>
              </MotifIconButton>
              <MotifIconButton
                className="top-icons btnLike"
                onClick={handleLikeClick}
                disabled={((activeProject.category !== "3") && isTableUnpublished)}
              >
                <MotifTooltip
                  placement="bottom"
                  contentClassName="tooltipWithoutClose"
                  hideCloseButton={true}
                  allowClick={false}
                  trigger={
                    commentaryLiked === 1 ? (
                      <img src={iconThumbsUpFilled} alt="Liked" />
                    ) : (
                      <img src={iconThumbsUp} alt="Like" />
                    )
                  }
                >
                 {((activeProject.category !== "3") && isTableUnpublished) ? tableTypePublishLike : "Like"}
                </MotifTooltip>
              </MotifIconButton>
              <MotifIconButton
                className="top-icons btnDislike"
                onClick={handleDisLikeClick}
                disabled={((activeProject.category !== "3") && isTableUnpublished)}
              >
                <MotifTooltip
                  placement="bottom"
                  contentClassName="tooltipWithoutClose"
                  hideCloseButton={true}
                  allowClick={false}
                  trigger={
                    commentaryLiked === -1 ? (
                      <img src={iconThumbsDownFilled} alt="Disliked" />
                    ) : (
                      <img src={iconThumbsDown} alt="Dislike" />
                    )
                  }
                >
                  {((activeProject.category !== "3") && isTableUnpublished) ? tableTypePublishDisLike : "Dislike"}
                </MotifTooltip>
              </MotifIconButton>
              <MotifIconButton
                className="top-icons btnGenAI"
                onClick={() => setSourceLinesModal()}
                disabled={((activeProject.category !== "3") && isTableUnpublished)}
              >
                <MotifTooltip
                  placement="bottom"
                  contentClassName="tooltipWithoutClose"
                  hideCloseButton={true}
                  allowClick={false}
                  trigger={<img src={iconSourceLines} alt="Source Lines" />}
                >
                  {((activeProject.category !== "3") && isTableUnpublished) ? tableTypePublishSourceLines : "Source Lines"}
                </MotifTooltip>
              </MotifIconButton>
              <MotifIconButton className="top-icons btnCopy" 
                disabled={((activeProject.category !== "3") && isTableUnpublished)}
                onClick={copyText}
              >
                <MotifTooltip
                  placement="bottom"
                  contentClassName="tooltipWithoutClose"
                  hideCloseButton={true}
                  allowClick={false}
                  trigger={<img src={iconCopy} alt="Copy" />}
                >
                  {((activeProject.category !== "3") && isTableUnpublished) ? tableTypePublishCopy : "Copy"}
                </MotifTooltip>
              </MotifIconButton>
              <MotifIconButton
                className="top-icons btnTransfer"
                onClick={() => handleTransferToPPTClick(false)}
                disabled={
                  ((activeProject.category !== "3") && isTableUnpublished) ||
                  (isTableCommentaryInitiated && !isTableCommentaryGenerated) ||
                  !isTableAnalysisGenerated ||
                  (isTableAnalysisGenerated === "In-Progress") ||
                  (isAllLineAnalysisNotGenerated && !isTableCommentaryGenerated) ||
                  ((lineAnalysisStatus === "In-Progress") && !isTableCommentaryGenerated) ||
                  showTransferToastTableAnalysis ||
                  !isCommentaryAvailable("Table")
                }
              >
                <MotifTooltip
                  placement="bottom"
                  contentClassName="tooltipWithoutClose"
                  hideCloseButton={true}
                  allowClick={false}
                  trigger={<img src={iconTransfer} alt="Transfer" />}
                >
                  {((activeProject.category !== "3") && isTableUnpublished) ? tableTypePublishTPPT : "Transfer to PPT"}
                </MotifTooltip>
              </MotifIconButton>
              <MotifIconButton
                className="top-icons btnExpand"
                onClick={() => setTableAnalysisModal()}
              >
                <MotifTooltip
                  placement="left"
                  contentClassName="tooltipWithoutClose"
                  hideCloseButton={true}
                  allowClick={false}
                  trigger={<img src={iconExpand} alt="Expand" />}
                >
                  Expand
                </MotifTooltip>
              </MotifIconButton>
            </div>
          </div>
          <div className="commentry-overflow">
            {tableAnalysis?.answer && (
              <p
                className="commentry-paragraph"
                id="commentary-para"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    utils.markdownToHtml(tableAnalysis.answer)
                  ),
                }}
              ></p>
            )}
            {showToastMessage && (
              <div className="toast-div-commentry">
                <MotifToast
                  onClose={function noRefCheck() {
                    setShowToastMessage(false);
                  }}
                  variant="success"
                  icons={{ toast: <img src={iconCheckCircle} alt="Success" /> }}
                >
                  Copied
                </MotifToast>
              </div>
            )}
            <MotifInlineMessage
              variant="warning"
              icons={{
                message: (
                  <img
                    src={iconWarningInfoCircle}
                    className="iconWarningInfoCircle"
                    alt="Warning"
                  />
                ),
              }}
              className="msgAIContainer"
            >
              <span className="msgAI">
                AI generated output may contain inaccuracies or errors. Verify
                output before use.
              </span>
            </MotifInlineMessage>
          </div>
        </div>
      </div>
    </>
  );
}

Commentary.propTypes = {
  isTableCommentaryInitiated: PropTypes.bool,
  isTableCommentaryGenerated: PropTypes.bool,
  isTableAnalysisGenerated: PropTypes.string,
  isAllLineAnalysisNotGenerated: PropTypes.bool,
  lineAnalysisStatus: PropTypes.string,
  showTransferToastTableAnalysis: PropTypes.bool,
  isCommentaryAvailable: PropTypes.func,
  isTableUnpublished: PropTypes.bool,
  handleTransferToPPTClick: PropTypes.func,
  selectedTableData: PropTypes.object,
  showToastTableAnalysis: PropTypes.bool,
  setShowToastTableAnalysis: PropTypes.func,
  setShowTableAnalysisModal: PropTypes.func,
  showTableAnalysisModal: PropTypes.bool,
  showToastCopy: PropTypes.bool,
  setShowToastCopy: PropTypes.func,
};
